import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  ChartBackground,
  ChartBlur,
  ChartLockIcon,
  ChartLockOverlay,
} from "../../screens/Admin/BandDashboardPage/BandDashboardPage.styles";
import { FiLock } from "react-icons/fi";

const ProductAnalyticsDonutChart = ({ bandProductAnalytics }) => {
  // Priprema podataka za donut chart
  const prepareChartData = () => {
    if (
      !bandProductAnalytics?.data?.products ||
      bandProductAnalytics.data.products.length === 0
    ) {
      return [];
    }

    const products = bandProductAnalytics.data.products;

    // Kreiraj podatke za chart na osnovu totalRevenue
    return products.map((product, index) => ({
      name: product.productName,
      value: product.totalRevenue,
      quantity: product.totalQuantitySold,
      orders: product.orderCount,
      avgPrice: product.averagePrice,
      color: getProductColor(index),
    }));
  };

  // Funkcija za dodelu boja produktima
  const getProductColor = (index) => {
    const colors = [
      "#FF6B35", // Primary orange
      "#E55A2B", // Darker orange
      "#FF8C42", // Lighter orange
      "#B8491A", // Darkest orange
      "#FF9F6B", // Light orange
      "#A0421D", // Dark brown orange
      "#FFAD85", // Very light orange
      "#8B3A16", // Very dark orange
    ];
    return colors[index % colors.length];
  };

  const chartData = prepareChartData();

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: "#1a1a1a",
            border: "2px solid #ff6b35",
            borderRadius: "8px",
            padding: "12px",
            color: "#ffffff",
            fontSize: "0.9rem",
            boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
          }}
        >
          <p
            style={{
              color: "#ff6b35",
              fontWeight: "bold",
              marginBottom: "8px",
              fontSize: "1rem",
            }}
          >
            {data.name}
          </p>
          <p style={{ margin: "4px 0", color: "#27ae60" }}>
            Prihod: <strong>{formatPrice(data.value)}</strong>
          </p>
          <p style={{ margin: "4px 0", color: "#cccccc" }}>
            Prodato: <strong>{data.quantity} kom</strong>
          </p>
          <p style={{ margin: "4px 0", color: "#cccccc" }}>
            Porudžbine: <strong>{data.orders}</strong>
          </p>
          <p style={{ margin: "4px 0", color: "#cccccc" }}>
            Prosečna cena: <strong>{formatPrice(data.avgPrice)}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  // Funkcija za formatiranje cene
  const formatPrice = (price) => {
    return new Intl.NumberFormat("sr-Latn-RS", {
      style: "currency",
      currency: "RSD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Custom label function
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.05) return null; // Ne prikazuj labele za male delove (manje od 5%)

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (chartData.length !== 0) {
    return (
      <ChartLockOverlay>
        <ChartBackground />
        <ChartBlur />
        <ChartLockIcon>
          <FiLock />
          <div className="lock-text">
            Potrebno je najmanje <span className="highlight">2 proizvoda</span>{" "}
            podataka
            <br />
            za prikazivanje grafa
          </div>
        </ChartLockIcon>
      </ChartLockOverlay>
    );
  }

  return (
    <div style={{ width: "100%", height: "300px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={100}
            innerRadius={40}
            fill="#8884d8"
            dataKey="value"
            animationBegin={0}
            animationDuration={800}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                stroke="#1a1a1a"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry) => (
              <span
                style={{
                  color: entry.color,
                  fontSize: "0.9rem",
                  fontWeight: "600",
                }}
              >
                {value}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Dodatne informacije ispod chart-a */}
      {/* <div
        style={{
          marginTop: "16px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "12px",
          fontSize: "0.8rem",
        }}
      >
        {chartData.map((product, index) => (
          <div
            key={index}
            style={{
              background: "rgba(255, 107, 53, 0.1)",
              border: "1px solid rgba(255, 107, 53, 0.3)",
              borderRadius: "6px",
              padding: "8px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: product.color,
                fontWeight: "bold",
                marginBottom: "4px",
                fontSize: "0.9rem",
              }}
            >
              {product.name}
            </div>
            <div style={{ color: "#27ae60", fontWeight: "bold" }}>
              {formatPrice(product.value)}
            </div>
            <div style={{ color: "#cccccc", fontSize: "0.75rem" }}>
              {product.quantity} kom • {product.orders} porudžbina
            </div>
          </div>
        ))}
      </div> */}
    </div>
  );
};

export default ProductAnalyticsDonutChart;
